import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { propEq, find, path, pipe, allPass, anyPass } from 'ramda'
import { DocumentView, ValueText, DocumentStatusComponent } from '@/features/Application/elements'
import { InformationTable, TableTitle } from '@/features/Application/elements/tables'
import getLabels from '@/features/Profile/EmploymentInfo/labels'
import useFinancialFiles from '@/hooks/use-financial-files'
import useRole from '@/hooks/use-role'
import MobileEmploymentVerification from './mobile'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 18px;
`

const isSchoolOrEmploymentVerification = pipe(
  anyPass([propEq('tag', 'school_verification'), propEq('tag', 'employment_verification')])
)

const EmploymentVerification = ({ data = {}, application, isLocked, isLoading, standalone }) => {
  const { t } = useTranslation()
  const userRole = useRole()
  const userId = application?.user_id
  const userFiles = userId ? application?.users?.[userId]?.files?.user_secure_files : undefined
  const secureFiles = useFinancialFiles(userFiles, userRole)

  const title = t('l.application.view.employment_verification.label', 'Employment Verification')
  const { rate, details = {} } = data

  const status = {
    poor: t('l.application.view.poor.label', 'Poor'),
    average: t('l.application.view.average.label', 'Average'),
    very_good: t('l.application.view.very_good.label', 'Very Good'),
    excellent: t('l.application.view.excellent.label', 'Excellent'),
    analyzing: t('l.application.view.analyzing.label', 'Analyzing')
  }

  const statusText = status[rate]
  const LABELS = getLabels(t)
  const columns = useMemo(
    () => [
      {
        Header: t('common.type', 'Type'),
        Cell: ({ row: { original } }) => {
          return (
            <ValueText type="small" isLocked={isLocked}>
              {LABELS[original?.[0]?.type] ?? '-'}
            </ValueText>
          )
        }
      },
      {
        Header: t('common.status', 'Status'),
        Cell: ({ row: { original } }) => (
          <DocumentStatusComponent
            status={original?.[0]?.status?.toLowerCase().replace(' ', '_')}
            isLocked={isLocked}
          />
        )
      },
      {
        Header: t('common.source', 'Source'),
        Cell: ({ row: { original } }) => {
          const document = pipe(
            find(
              allPass([
                propEq('document_type', original?.[0]?.type),
                isSchoolOrEmploymentVerification
              ])
            )
          )(secureFiles)

          const docType = document?.tag?.split('_')?.[0]
          const documentType = docType === 'school' ? 'employment' : docType
          const documentStatus = path(
            ['trust_score', 'trustworthiness', documentType, 'details', 0, 'status'],
            application
          )
          const disabled = documentStatus !== 'verified' || original?.deleted === '1'

          return (
            <DocumentView
              onClick={document?.onClick}
              isDisabled={disabled}
              size="small"
              isLocked={isLocked}
              documentStatus={standalone ? 'standalone' : documentStatus}
            />
          )
        }
      }
    ],
    [isLocked, isLoading]
  )

  const tableData = [details]

  return (
    <Wrapper>
      <TableTitle
        title={title}
        showStatus={true}
        statusType={rate}
        statusText={statusText}
        isLocked={isLocked}
        isLoading={isLoading}
      />
      <InformationTable data={tableData} columns={columns} showBorder isLoading={isLoading} />
    </Wrapper>
  )
}

export { MobileEmploymentVerification }
export default EmploymentVerification
